import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, Rechnung, RechnungAnzeigeStatus, RechnungTrefferSeite,
              Unternehmen } from "@kundeninfo/api-client";
import { RechnungDetail, useRechnungsUmfeld } from "../components/RechnungDetail.js";
import { apiMeldung, pdfMeldungsStil } from "../components/vorgangsdetail.js";
import type { PdfMeldung } from "../components/vorgangsdetail.js";
import type { RechnungenClient } from "./KundeRechnungen.js";
import type { VerlassenWaechter } from "../router.js";
import { formatBetrag, formatDatum } from "../format.js";
import { RECHNUNG_ANZEIGE_LABEL } from "../vorgangsstatus.js";
import { RECHNUNGS_FILTER, statusseZuFilter } from "../bereichsfilter.js";
import type { RechnungsFilter } from "../bereichsfilter.js";
import { Filterkopf } from "../components/Filterkopf.js";
import type { FilterChip } from "../components/Filterkopf.js";
import { Objektliste, Objektzeile } from "@kundeninfo/ui";
import type { StatusArt } from "@kundeninfo/ui";
import "./BereichRechnungen.css";

/**
 * Der Rechnungs-BEREICH: Spaltenliste links (Filterkopf, Belegzeilen, Blätterung),
 * vollwertiges Lese-Detail rechts — die Gestalt seit dem 23.09.2026 (Entscheid Dirk am
 * Live-System, Aufgabe 7), der letzte der vier Objektbereiche, der sie zurückbekommt.
 * Zeile für Zeile nach demselben Muster wie die Schwestern views/BereichAngebote.tsx,
 * BereichAuftraege.tsx und BereichArtikel.tsx. Was dort im Kopf steht (warum ein Hook und
 * keine Komponente; warum der Bereich kein zweites Detail ist), gilt hier gleichlautend.
 * Dieser Kopf nennt nur, was die RECHNUNG unterscheidet: KEIN Anlegen (ein Beleg entsteht
 * am Auftrag, nicht auf Zuruf), eine MEHRFACH wählbare Chip-Leiste statt einfacher Wahl,
 * und der Formatchip der Zeile.
 *
 * Die ANORDNUNG ist zweimal gewechselt und steht jetzt wieder, wo sie anfing: Spalte und
 * Detail bis zum 07.09.2026, dann „Filterkopf statt zweiter Spalte" (alles untereinander
 * in voller Breite), und seit dem 23.09.2026 wieder Spalte und Detail. Vom Umbau des
 * 07.09. BLEIBT, was sich dabei als richtig erwiesen hat: der Filterkopf als EIN Baustein
 * für alle vier Bereiche (er steht jetzt oben IN der Spalte, `lage="spalte"`), der Filter
 * in der Adresse und die Detail-Bausteine. Was geht, ist allein das Untereinander — und
 * mit ihm der bereichseigene Sprung-Mechanismus (ehem. components/Bereichsfaltung.tsx),
 * denn die Faltung auf schmalem Gerät macht wieder der Rahmen.
 *
 * Die ZEILEN zeichnet seit dem 23.09.2026 der gemeinsame Baustein `Objektliste`/
 * `Objektzeile` aus @kundeninfo/ui — dieselbe Zeile wie in den Schwesterbereichen.
 *
 * **KEINE Kunden-Gliederung mehr.** Bis zum Paket „Rechnungsstatus" war die Spalte
 * ZWEISTUFIG: erst einen Kunden suchen, dann seine Belege sehen — der einzige Weg, solange
 * die API nur
 * `GET /api/customers/{id}/invoices` kannte. Mit `GET /api/invoices` (globale Liste,
 * Blätterung, Suche über Nummer UND Kundenname) fällt dieser Umweg weg, und Dirks Entscheid
 * vom 06.09. ist eindeutig: Wer nach Kunden gliedern will, geht in den Kundenbereich — hier
 * wird nach STATUS gearbeitet. Die erste Stufe ist restlos verschwunden; der Kundenbezug
 * einer Zeile steht in ihr und führt weiterhin über „Zum Kunden" in die Akte.
 *
 * **Der Filterkopf trägt EINE Chip-Leiste** (components/Filterkopf.tsx): „Alle
 * Rechnungen", dann je ein Chip für Entwurf, Festgeschrieben, Bezahlt und Storniert,
 * mehrfach wählbar, Vorgabe alle an. Bis zum 07.09.2026 waren das ZWEI Bedienelemente über
 * denselben vier Gruppen — eine Filterliste in der Spalte (einfach wählbar, in der
 * Adresse) und darunter eine Chip-Leiste (mehrfach wählbar, nicht in der Adresse). Dirks
 * Befund am Live-System: Die Spalte hatte nur Filterfunktion und trug dabei die Objekte
 * selbst. Beide sind zu einer Leiste verschmolzen.
 *
 * **Die Chip-Auswahl steht in der ADRESSE** (`/rechnungen/entwurf,bezahlt`; ohne Segment
 * heisst „alle", `keine` heisst „keiner"). Die Begründung steht am Routentyp (router.ts):
 * Eine Statusliste IST ein Bildschirm, und ein Lesezeichen darf darauf zeigen. Sie
 * überlebt den Bereichswechsel nicht — die Hauptnavigation führt auf `/rechnungen`.
 *
 * **Serverseitig gefiltert und geblättert, auch für die Chips.** Ein Filter NACH der
 * Seitenbildung liesse `total` und Seitengrösse lügen: Seite 1 zeigte drei von „50", Seite 2
 * wäre leer. Deshalb reist die Chip-Auswahl als `status`-Mehrfachwert an die API (siehe
 * `searchInvoices` im Klienten). Ist KEIN Chip aktiv, wird gar nicht erst gefragt — die
 * Antwort steht fest, und die Spalte sagt es stattdessen.
 *
 * **Der Kundenbezug** ist nicht bloss Beiwerk: Eine Rechnung wird über IHREN KUNDEN gelesen
 * und geschrieben (`getInvoice(kundeId, …)`), und dieselbe Kennung braucht der Verweis „Zum
 * Kunden". Er kommt aus der Trefferzeile und wird festgehalten, solange derselbe Beleg offen
 * ist (`bezug`) — sonst risse ein Filterwechsel, der die Zeile aus der Trefferseite drängt,
 * das offene Detail zu. Ein Lesezeichen auf einen Beleg, den die aktuelle Trefferseite nicht
 * enthält, bekommt deshalb eine Auskunft statt einer leeren Fläche (dreistufig, siehe unten).
 *
 * **Die Auftrag→Rechnung-Naht endet HIER** (Ruling des Controllers vom 06.09.2026): Der
 * Auftrags-Bereich leitet die Rechnung ab und meldet sie an App.tsx, die Adresse wechselt
 * auf `/rechnungen/<neueId>`, und dieser Bereich nimmt sie beim Betreten auf — samt Kunde,
 * sodass das Detail sofort steht. Die Kundenakte bleibt davon UNBERÜHRT; ihr eigener Weg
 * über den Rechnungen-Reiter besteht unverändert weiter.
 *
 * **CSS:** eigener Block `brng__` für das Drumherum. Was die BAUSTEINE mitbringen, behält
 * seine Klassen (`krg__` für das Detail, `fkop__` für den Filterkopf, die Spaltenlage
 * `fkop--spalte`); geteilt wird über Ansichten hinweg KEIN Block. Die ZEILE selbst trägt
 * seit dem 23.09.2026 den Block `ki-objekt__` des gemeinsamen Bausteins (packages/ui/src/
 * liste/Objektliste.css) und keinen eigenen mehr — der bereichseigene Sprung-Mechanismus
 * `bfal__` (ehem. components/Bereichsfaltung.tsx) ist mit der Spalte entfallen.
 */

/** Der Bereich braucht alles, was der Akten-Reiter braucht — plus die GLOBALE Suche, den
 *  Einzelabruf eines Belegs (die Trefferzeile ist mager) und die Auflösung des
 *  Kundennamens für einen Bezug, der nicht aus einer Trefferzeile kommt (die Naht aus dem
 *  Auftrag). `getCustomers` braucht er seit dem Wegfall der Kundensuche NICHT mehr. */
export type BereichRechnungenClient = RechnungenClient
  & Pick<ApiClient, "searchInvoices" | "getInvoice" | "getCustomer">;

/**
 * Die am Auftrag abgeleitete Rechnung, wie sie in DIESEM Bereich ankommt.
 *
 * Der Kunde reist mit, weil er dort feststeht: Die frische Rechnung ist ein Entwurf ohne
 * Nummer, und ihn über die Trefferliste zu suchen wäre ein Umweg um eine Auskunft, die
 * schon da ist. Der NAME steht in der Trefferzeile des Auftrags-Bereichs nicht zur
 * Verfügung und wird deshalb hier einmal aufgelöst (`getCustomer`).
 */
export type RechnungAusAuftrag = {
  kundeId: string;
  rechnung: Rechnung;
};

export type BereichRechnungenProps = {
  /** Steht der Bereich gerade auf dem Bildschirm? Der Hook wird — Regeln der Hooks — auch
   *  an anderen Adressen gerufen; ohne diesen Riegel liefe seine Suche überall mit. */
  aktiv: boolean;
  client: BereichRechnungenClient;
  /** Die sichtbaren Konzernunternehmen — zur Auflösung der Kürzel. */
  companies: Unternehmen[];
  /** Die eingeschalteten Status-Gruppen aus der Adresse (`/rechnungen/<filter>`) — alle
   *  vier heisst „Alle Rechnungen", die leere Liste heisst „keine". */
  filter: readonly RechnungsFilter[];
  /** Die geöffnete Rechnung aus der Adresse — `null` heisst „keine". */
  rechnungId: string | null;
  /**
   * Die Zeile, die die Liste als gewählt zeigt, solange KEIN Objekt offen ist — am Telefon
   * die zuletzt geöffnete, damit der Rückweg die Auswahl behält (App.tsx, Hausregel
   * „Scroll bei Neuzeichnen"). Weggelassen: keine.
   */
  markiertId?: string | null;
  /** Die eben am Auftrag abgeleitete Rechnung — `null`, wenn niemand herüberspringt. */
  ausAuftrag: RechnungAusAuftrag | null;
  /** Aufgenommen heisst VERBRAUCHT: Der Bereich meldet das sofort, damit die Übergabe genau
   *  einen Sprung überlebt (dieselbe Verfallsregel wie bei der Angebot→Auftrag-Naht). */
  onAusAuftragAufgenommen: () => void;
  /** Einen Beleg aufschlagen bzw. zuklappen (`null`) — die Chip-Auswahl bleibt, wie sie
   *  ist. Der Bereich navigiert NICHT selbst: Die Adresse kennt App.tsx. */
  onOeffnen: (rechnungId: string | null) => void;
  /** Die Chip-Auswahl ändern. Sie klappt den offenen Beleg zu, weil er in der neuen Liste
   *  nicht stehen muss — `/rechnungen/entwurf/<id>` über einer Liste ohne diese Zeile wäre
   *  eine Adresse, die etwas anderes behauptet, als zu sehen ist. */
  onFilter: (filter: readonly RechnungsFilter[]) => void;
  /** „Zum Kunden": in die Akte, mit dem Herkunfts-Rückweg vom 05.09. */
  onZumKunden: (kundeId: string) => void;
  /** Der Verlassen-Wächter des Routers (Hausmuster, siehe KundeStammdaten): Bei offenem
   *  Entwurf fragt jede Navigation nach. Weglassbar — dann fragt niemand, was für einen
   *  Prüfharnisch ohne Router genau richtig ist. */
  registriereVerlassenWaechter?: (frage: VerlassenWaechter) => () => void;
};

export type BereichRechnungenTeile = {
  /** Für `MeSchale.list` — Filterkopf, Belegzeilen, Blätterung. */
  spalte: ReactElement;
  /** Für `MeSchale.detail` — der gewählte Beleg als Lese-Detail. */
  detail: ReactElement;
};

/** Wie viele Treffer auf eine Seite gehen — dieselbe Zahl wie in den Schwesterbereichen. */
const JE_SEITE = 20;

/** Die Chips der Leiste: die vier Gruppen in der Reihenfolge des Lebenslaufs, mit ihren
 *  Beschriftungen aus der Beschriftungstabelle (vorgangsstatus.ts) — nicht abgetippt, damit
 *  ein umbenannter Status hier nicht ein zweites Mal gepflegt werden muss. */
const CHIPS: readonly FilterChip<RechnungsFilter>[] = RECHNUNGS_FILTER.map((f) => (
  { id: f.id, label: RECHNUNG_ANZEIGE_LABEL[f.status] }));

/**
 * Die drei Rechnungsformate, KURZ beschriftet (Nachtrag Aufgabe 6) — für den Chip der
 * Zeile. Dieselben Schlüssel und dieselben Wörter wie in KundeStammdaten.tsx und
 * RechnungDetail.tsx; die Tabelle steht ein drittes Mal hier, weil `web` keinen
 * gemeinsamen Ablageort für Fachwörter hat, der nicht ein neuer geteilter Block wäre.
 */
const RECHNUNGSFORMATE: { wert: string; kurz: string }[] = [
  { wert: "PDF_A3", kurz: "PDF" },
  { wert: "ZUGFERD_EN16931", kurz: "ZUGFeRD" },
  { wert: "XRECHNUNG_30", kurz: "XRechnung" },
];

/**
 * Die Zuordnung Anzeige-Status → Chip-ART (der Baustein kennt nur fünf Arten, siehe
 * packages/ui/src/liste/Objektliste.tsx).
 *
 * Die FARBWAHL ist unverändert die von vorher (`brng__status--*`), mit einer Feinheit, die
 * erhalten bleiben MUSS: `issued` und `paid` sind VERSCHIEDEN eingefärbt. In einer Liste, in
 * der beide nebeneinander stehen („Alle Rechnungen"), ist genau dieser Unterschied die Frage,
 * die man stellt.
 *
 * Ruling des Controllers (23.09.2026): `ISSUED` ist `laeuft` — eine festgeschriebene
 * Rechnung ist eine OFFENE Forderung, dieselbe Aussage wie „gesendet" beim Angebot und
 * „aktiv" beim Auftrag. `DRAFT` ist `aus` — noch nichts passiert. Damit bleiben alle vier
 * Rechnungsstatus farblich verschieden (vorher war `issued` neutral
 * `--ki-surface-raised`).
 *
 * Als `Record` über den Statustyp getippt: Kommt eine Gruppe hinzu, meldet das der Typprüfer.
 */
const STATUS_ART: Record<RechnungAnzeigeStatus, StatusArt> = {
  DRAFT: "aus",
  ISSUED: "laeuft",
  PAID: "ok",
  CANCELLED: "warnung",
};

/** Klasse und ARIA-Rolle je Meldungsart im Block DIESER Ansicht (siehe vorgangsdetail.ts).
 *  Eigener Blockname statt des geteilten `krg__`: Eine Stilanpassung am Akten-Reiter soll
 *  den Bereich nicht still mitverändern (Kollisions-Lehre 01.09.). */
const MELDUNGS_STIL = pdfMeldungsStil("brng");

/** Der Kundenbezug des geöffneten Belegs — einmal aus der Trefferzeile aufgelöst und danach
 *  festgehalten (siehe den Kopf dieser Datei). */
type Kundenbezug = { rechnungId: string; kundeId: string; kundeName: string };

/**
 * Die QUITTUNG des Bereichs — samt der Rechnung, zu der sie gehört.
 *
 * Sie hängt an der ADRESSE und nicht am Klick, der sie ausgelöst hat (Lehre aus der
 * Fixrunde zu Aufgabe 5): Adressen wechseln auch ohne Zeilenklick — Zurück-Taste, Rückweg
 * im Kopf des Einspalters, ein Lesezeichen auf eine andere Rechnung. Bliebe die Meldung
 * dabei stehen, meldete sie über der NEUEN Rechnung einen Erfolg, den es dort nie gab.
 *
 * Als mitgeführte Kennung statt als Effekt, der sie beim Adresswechsel löscht: Der Sprung
 * zur Korrektur setzt seine Quittung FÜR die andere Rechnung, noch bevor die Adresse
 * gewechselt hat — ein Löscheffekt räumte genau sie wieder ab.
 */
type Quittung = { fuer: string | null; meldung: PdfMeldung };

export function useBereichRechnungen(p: BereichRechnungenProps): BereichRechnungenTeile {
  const { client, companies, aktiv, filter, rechnungId } = p;

  const [seite, setSeite] = useState<RechnungTrefferSeite | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [suche, setSuche] = useState("");
  const [offset, setOffset] = useState(0);
  /** Hochgezählt, wenn die Trefferseite einen Stand noch nicht kennen kann — nach jeder
   *  Änderung, die die GRUPPE oder die Nummer eines Belegs betrifft (siehe
   *  `fortschreiben`). */
  const [listeVersion, setListeVersion] = useState(0);

  const [bezug, setBezug] = useState<Kundenbezug | null>(null);
  const [satz, setSatz] = useState<Rechnung | null>(null);
  const [satzFehler, setSatzFehler] = useState<string | null>(null);
  const [quittung, setQuittung] = useState<Quittung | null>(null);

  /**
   * WELCHE Rechnung gerade aufgeschlagen ist — als Kennung, nicht als Ja/Nein.
   *
   * Ein blosses `bearbeitung: boolean` überlebte den Adresswechsel (Befund aus Aufgabe 3):
   * Wer aus dem offenen Beleg heraus zurückging (Rückweg im Kopf, Zurück-Taste), landete
   * auf der Objektliste — und die blieb für immer gesperrt, denn ihr Riegel hängt genau an
   * dieser Angabe und es gab keinen Bedienweg mehr, sie zu lösen. Über die Kennung ist der
   * Zustand an die ADRESSE gebunden: Ist keine Rechnung offen (oder eine andere), ist auch
   * nichts in Arbeit.
   */
  const [bearbeitetId, setBearbeitetId] = useState<string | null>(null);
  const bearbeitung = bearbeitetId !== null && bearbeitetId === rechnungId;

  /** Die Auswahlhilfen (Platzhalter, Länder) — einmal für den ganzen Bereich, wie im
   *  Akten-Reiter. */
  const umfeld = useRechnungsUmfeld(client);

  /*
   * Beim VERLASSEN des Bereichs fällt alles Angefangene weg (Muster aus Aufgabe 3).
   *
   * Quittung, aufgeschlagener Beleg, Suchtext und Blätterung sind Bearbeitungszustände
   * ohne eigene Adresse. Blieben sie über einen Menüwechsel hinweg stehen, käme man mit der
   * Meldung einer längst verlassenen Absicht zurück — und die Liste zeigte beim nächsten
   * Betreten heimlich eine Teilmenge, ohne dass die Adresse es sagte. Der LADEFEHLER geht
   * mit: Ein Fehler von vorhin ist keine Auskunft über den Bestand von jetzt.
   *
   * Die CHIP-Auswahl steht seit dem 07.09.2026 in der ADRESSE und gehört deshalb NICHT
   * mehr in diesen Effekt: Sie räumt sich von selbst ab, weil die Hauptnavigation auf
   * `/rechnungen` führt — auf die Adresse ohne Filtersegment, also auf „alle". Sie hier
   * zusätzlich zurückzusetzen hiesse, den Zustand an zwei Stellen zu führen.
   */
  useEffect(() => {
    if (aktiv) return;
    setBearbeitetId(null);
    setQuittung(null);
    setSuche("");
    setOffset(0);
    setLadeFehler(null);
  }, [aktiv]);

  /**
   * Die Statusauswahl, mit der gefragt wird — und `null` heisst „ohne Statusparameter".
   *
   * Drei Fälle, und alle drei tragen Bedeutung:
   *  - ALLE Chips an → KEIN Statusparameter (das ist etwas anderes als „alle vier
   *    aufzählen": Käme je eine fünfte Gruppe hinzu, stünde sie sofort drin),
   *  - eine Teilmenge → genau diese,
   *  - KEIN Chip an → gar nicht fragen (`keineAuswahl`). Eine leere Liste an die API zu
   *    schicken ginge über die Abfragezeichenkette nicht (ein fehlender Parameter IST das
   *    Weglassen), und die Antwort steht ohnehin fest.
   */
  const alleChips = filter.length >= RECHNUNGS_FILTER.length;
  const keineAuswahl = filter.length === 0;
  const statusAuswahl: readonly RechnungAnzeigeStatus[] | null =
    alleChips ? null : statusseZuFilter(filter, RECHNUNGS_FILTER);

  const searchInvoices = client.searchInvoices;
  // Die Chip-Auswahl geht als ZEICHENKETTE in die Abhängigkeiten: Ein Array wäre bei jedem
  // Zeichnen eine neue Referenz und liesse den Effekt endlos laufen.
  const statusSchluessel = statusAuswahl === null ? "" : [...statusAuswahl].join(",");
  useEffect(() => {
    if (!aktiv) return;
    if (keineAuswahl) {
      // Nicht fragen — und ausdrücklich eine LEERE Seite hinstellen statt der alten:
      // Ein stehen gebliebener Treffer unter einer Leiste ohne aktiven Chip wäre die
      // Behauptung, er sei durch den Filter gekommen.
      setSeite({ items: [], total: 0 });
      setLadeFehler(null);
      return;
    }
    let abgebrochen = false;
    searchInvoices({
      ...(statusAuswahl === null ? {} : { status: statusAuswahl }),
      ...(suche === "" ? {} : { suche }),
      limit: JE_SEITE,
      offset,
    })
      .then((antwort) => {
        if (abgebrochen) return;
        setSeite(antwort);
        // Der Ladefehler fällt im ERFOLGSFALL (Lehre aus Etappe 1): Bliebe er stehen,
        // stünde die Fehlermeldung von vorhin über einer frisch geladenen Liste.
        setLadeFehler(null);
      })
      .catch(() => {
        if (!abgebrochen) {
          setLadeFehler("Die Rechnungen konnten nicht geladen werden. "
            + "Bitte die Seite neu laden.");
        }
      });
    return () => { abgebrochen = true; };
    // `statusAuswahl` steht hier als Zeichenkette (`statusSchluessel`): Ein Array wäre bei
    // jedem Zeichnen eine neue Referenz und liesse den Effekt endlos laufen.
  }, [aktiv, searchInvoices, statusSchluessel, keineAuswahl, suche, offset, listeVersion]);

  /**
   * Den Kundenbezug des geöffneten Belegs aus der Trefferseite auflösen — EINMAL je Beleg.
   * Steht er schon, bleibt er: Sonst risse ein Filterwechsel, der die Zeile aus der
   * Trefferseite drängt, das offene Detail wieder zu.
   */
  useEffect(() => {
    if (rechnungId === null) { setBezug(null); return; }
    const zeile = seite?.items.find((t) => t.id === rechnungId);
    setBezug((alt) => {
      if (alt !== null && alt.rechnungId === rechnungId) return alt;
      if (zeile === undefined) return null;
      return { rechnungId, kundeId: zeile.customerId, kundeName: zeile.customerName };
    });
  }, [rechnungId, seite]);

  /*
   * Der NAME des Kunden, wenn er nicht aus einer Trefferzeile kam (die Naht aus dem Auftrag
   * reicht nur die Kennung herüber). Ein Abruf, und er darf scheitern: Dann steht die
   * Überschrift ohne Namen da, und alles andere — Detail, „Zum Kunden" — hängt ohnehin an
   * der Kennung.
   */
  useEffect(() => {
    if (bezug === null || bezug.kundeName !== "") return;
    let abgebrochen = false;
    const { kundeId } = bezug;
    client.getCustomer(kundeId)
      .then((k) => {
        if (!abgebrochen) {
          setBezug((alt) => (alt === null || alt.kundeId !== kundeId
            ? alt : { ...alt, kundeName: k.companyName }));
        }
      })
      .catch(() => { /* Beschriftung — kein Aufhalten */ });
    return () => { abgebrochen = true; };
  }, [client, bezug]);

  /*
   * Den VOLLEN Beleg lesen — die Trefferzeile trägt bewusst nur die magere Gestalt eines
   * Sucheinstiegs (kein `version`, keine Texte, kein Zahlungssatz).
   *
   * Der Riegel über `satzRef` ist dieselbe Regel wie in den Schwesterbereichen: Gelesen
   * wird nur, was noch nicht da ist. Alles Weitere — nach dem Speichern, nach dem
   * Festschreiben, nach einer 409 — stösst der Baustein selbst über `onNeuLaden` an.
   */
  const satzRef = useRef<Rechnung | null>(null);
  satzRef.current = satz;
  useEffect(() => {
    if (!aktiv || bezug === null) { setSatz(null); setSatzFehler(null); return; }
    if (satzRef.current !== null && satzRef.current.id === bezug.rechnungId) return;
    let abgebrochen = false;
    setSatz(null);
    setSatzFehler(null);
    client.getInvoice(bezug.kundeId, bezug.rechnungId)
      .then((r) => { if (!abgebrochen) setSatz(r); })
      .catch((e) => {
        if (!abgebrochen) {
          setSatzFehler(apiMeldung(e, "Die Rechnung konnte nicht geladen werden."));
        }
      });
    return () => { abgebrochen = true; };
  }, [aktiv, client, bezug]);

  /*
   * Die Auftrag→Rechnung-Naht AUFNEHMEN: Kundenbezug übernehmen, Beleg aufschlagen.
   *
   * Der Beleg kommt FERTIG herüber (der Auftrags-Bereich hat ihn eben angelegt), also wird
   * er als `satz` gesetzt statt nachgeladen. Sofort als verbraucht melden — die Übergabe
   * soll genau EINEN Sprung überleben.
   *
   * **Die STELLUNG dieses Effekts ist bindend**, und zwar gleich zweifach:
   *
   *  - NACH dem Aufräum-Effekt (`aktiv`) — beim Betreten des Bereichs läuft der zuerst und
   *    gibt sich mit `aktiv` zufrieden, sodass er den eben gesetzten Zustand nicht gleich
   *    wieder abräumt.
   *  - NACH dem Satz-Lade-Effekt darüber. Der räumt `satz` ab, solange es keinen Bezug
   *    gibt (`setSatz(null)`), und Effekte EINES Durchlaufs werden gemeinsam angewandt:
   *    Stünde diese Naht davor, gewönne das Abräumen — der mitgereichte Beleg wäre im
   *    selben Atemzug wieder weg, und der Bereich läse ihn nach, obwohl er ihn schon
   *    hatte (gemessen).
   */
  const ausAuftrag = p.ausAuftrag;
  const onAusAuftragAufgenommen = p.onAusAuftragAufgenommen;
  useEffect(() => {
    if (!aktiv || ausAuftrag === null) return;
    setBezug((alt) => (alt !== null && alt.kundeId === ausAuftrag.kundeId
      // Denselben Kunden nicht neu setzen: Der Name steht dann schon da, und ein leeres
      // Namensfeld löste einen überflüssigen `getCustomer` aus.
      ? { ...alt, rechnungId: ausAuftrag.rechnung.id }
      : { rechnungId: ausAuftrag.rechnung.id, kundeId: ausAuftrag.kundeId, kundeName: "" }));
    setSatz(ausAuftrag.rechnung);
    setSatzFehler(null);
    setBearbeitetId(ausAuftrag.rechnung.id);
    setQuittung({
      fuer: ausAuftrag.rechnung.id,
      meldung: {
        text: "Aus dem Auftrag abgeleitet — Positionen, Texte und der Zahlungssatz sind "
          + "übernommen. Die Rechnung ist noch ein Entwurf.",
        art: "gut",
      },
    });
    onAusAuftragAufgenommen();
  }, [aktiv, ausAuftrag, onAusAuftragAufgenommen]);

  /**
   * Verlassen-Wächter (Hausmuster, siehe KundeStammdaten): Solange ein ENTWURF offen steht,
   * fragt JEDE Navigation nach — der Rückweg im Kopf des Einspalters ebenso wie die
   * Zurück-Taste. Ohne ihn verwarf ein Rückweg die Kopfeingaben stumm.
   *
   * Nur am Entwurf: Ein festgeschriebener oder stornierter Beleg zeigt überhaupt kein Feld
   * (der Baustein zeichnet dort die nur-lesende Fassung) — eine Rückfrage wäre dort eine,
   * die immer verneint werden kann und deshalb ungelesen weggeklickt würde.
   *
   * Registriert wird am offenen FORMULAR, nicht erst an einer erkannten Änderung: Der
   * Entwurf lebt im Baustein, und der Bereich sieht ihn nicht.
   *
   * Seit der Modal-Hausregel (06.09.2026) ist „aufgeschlagen" nicht mehr dasselbe wie
   * „Formular offen": Über dem Lese-Detail des Entwurfs steht ein Panel, und Speichern wie
   * Abbrechen führen aus dem Panel zurück aufs Detail — nicht in die Liste. Der Baustein
   * meldet deshalb, ob sein Panel steht (`onBearbeitungWechsel`); ohne diese Auskunft
   * fragte der Wächter auch am blossen Lese-Detail nach, wo es nichts zu verlieren gibt.
   */
  const [formularOffen, setFormularOffen] = useState(false);
  const entwurfOffen = bearbeitung && formularOffen
    && satz !== null && satz.status === "DRAFT";
  const registriereVerlassenWaechter = p.registriereVerlassenWaechter;
  useEffect(() => {
    if (!entwurfOffen || registriereVerlassenWaechter === undefined) return;
    return registriereVerlassenWaechter(() => window.confirm(
      "Diese Rechnung ist noch in Bearbeitung. Nicht gespeicherte Eingaben gehen "
      + "verloren. Wirklich verlassen?"));
  }, [entwurfOffen, registriereVerlassenWaechter]);

  /** Die Quittung gehört der Rechnung, über der sie steht (siehe `Quittung`). */
  const hinweis = quittung !== null && quittung.fuer === rechnungId
    ? quittung.meldung : null;

  function melde(meldung: PdfMeldung | null): void {
    setQuittung(meldung === null ? null : { fuer: rechnungId, meldung });
  }

  /**
   * Der Baustein hat den Beleg weitergedreht (Version und Beträge aus dem Positions-Editor,
   * frischer Stand nach einem Schreibweg). Er wird HIER fortgeschrieben — die Version, mit
   * der die nächsten Knöpfe rechnen, kommt aus diesem Satz.
   *
   * **Die Trefferliste wird nur dann nachgezogen, wenn sich die GRUPPE oder die NUMMER
   * geändert hat** (Festschreiben, Stornieren, Bezahlt-Kennzeichen). Das ist die
   * Umsetzung zweier Regeln zugleich:
   *
   *  - Die Liste muss nachziehen, sonst stünde ein eben bezahlter Beleg weiter unter
   *    „Festgeschrieben" — und der Filter, unter dem man ihn sucht, zeigte auf den falschen
   *    Eintrag.
   *  - Sie darf NICHT bei jedem Positionsschritt nachziehen (Ruling der Etappe: keine
   *    Neuladung während einer offenen Bearbeitung). Ein Positionsschritt ändert Betrag und
   *    Version, aber weder Gruppe noch Nummer; die Liste holt den frischen Betrag beim
   *    nächsten ohnehin fälligen Laden.
   *
   * Gefahrlos ist das Nachziehen hier, weil die Version NICHT aus der Liste kommt: Die
   * Trefferzeile ist mager und führt keine (anders als in der Fassung vor diesem Paket, in
   * der die Spalte die vollen Belege hielt).
   */
  function fortschreiben(neu: Rechnung): void {
    const alt = satzRef.current;
    setSatz(neu);
    const gruppeGeaendert = alt === null
      || alt.status !== neu.status
      || alt.paidAt !== neu.paidAt
      || alt.invoiceNumber !== neu.invoiceNumber;
    if (gruppeGeaendert) setListeVersion((v) => v + 1);
  }

  /** Der Baustein bittet um einen frischen Stand (nach einer 409). Erst dann — und nur
   *  dann — werden Beleg UND Liste neu gelesen. */
  async function satzNeuLaden(): Promise<void> {
    if (bezug === null) return;
    setListeVersion((v) => v + 1);
    try {
      setSatz(await client.getInvoice(bezug.kundeId, bezug.rechnungId));
      setSatzFehler(null);
    } catch (e) {
      setSatzFehler(apiMeldung(e, "Die Rechnung konnte nicht geladen werden."));
    }
  }

  /**
   * Eine andere Rechnung ADRESSIEREN — mehr nicht: Der Kasten bleibt zu, und die
   * Belegliste bleibt bedienbar.
   *
   * Genau das ist der Unterschied zum Akten-Reiter, und er ist Absicht: Wer im Bereich
   * durch die Belege blättert, klickt Zeile für Zeile. Öffnete jeder Klick gleich den
   * Kasten, sperrte er damit die Liste — und der nächste Beleg wäre erst nach einem
   * „Abbrechen" erreichbar. Dieselbe Teilung führen die Schwesterbereiche: Die Adresse
   * wählt, „Bearbeiten" öffnet.
   */
  function adresseZu(id: string, meldung: PdfMeldung | null = null): void {
    setQuittung(meldung === null ? null : { fuer: id, meldung });
    p.onOeffnen(id);
  }

  /**
   * Eine andere Rechnung AUFSCHLAGEN — Adresse und Kasten. Diesen Weg gehen nur die
   * Sprünge zwischen Original und Korrektur und die frisch angelegte Korrektur: Wer sie
   * auslöst, kommt aus einem offenen Kasten und will wieder in einem landen.
   *
   * Der KUNDENBEZUG wandert mit, statt aus der Trefferseite geholt zu werden: Original und
   * Korrektur gehören demselben Kunden (die Korrektur entsteht aus dem Original), aber der
   * Sprungpartner muss in der aktuellen Liste nicht stehen — unter „Storniert" ist die
   * Korrektur ein Entwurf und damit ausgefiltert, und selbst ohne Filter kann sie auf einer
   * anderen Seite liegen. Ohne diese Zeile landete ein gelungener Sprung vor „steht nicht
   * in der Trefferliste" (gemessen).
   */
  function aufschlagen(id: string, meldung: PdfMeldung | null = null): void {
    setBezug((alt) => (alt === null ? null : { ...alt, rechnungId: id }));
    adresseZu(id, meldung);
    setBearbeitetId(id);
  }

  /**
   * Die frisch angelegte (oder schon bestehende) Korrektur aufnehmen.
   *
   * Der Beleg kommt FERTIG von der API zurück und wird deshalb gesetzt statt nachgelesen;
   * den Kundenbezug zieht `aufschlagen` mit (siehe dort — die Korrektur steht unter einem
   * Statusfilter nicht in der Liste). Die Trefferliste wird nachgezogen, weil sich ZWEI
   * Sätze geändert haben: Die Korrektur ist dazugekommen, und das Original trägt nun den
   * Rückverweis.
   */
  function korrekturAufnehmen(korrektur: Rechnung, text: string): void {
    setSatz(korrektur);
    setSatzFehler(null);
    setListeVersion((v) => v + 1);
    aufschlagen(korrektur.id, { text, art: "gut" });
  }

  /** Filter ändern heisst: zurück auf die erste Seite. Sonst stünde man nach dem Filtern
   *  auf Seite 7 eines Ergebnisses mit einer Seite und sähe nichts. */
  function filtern(setzen: () => void): void {
    setzen();
    setOffset(0);
  }

  /**
   * Die Chip-Auswahl ändern. Der offene Beleg wird dabei auch in der ADRESSE zugeklappt:
   * Er muss in der neuen Liste nicht stehen, und `/rechnungen/entwurf/<id>` über einer
   * Liste ohne diese Zeile wäre eine Adresse, die etwas anderes behauptet, als zu sehen
   * ist.
   */
  function filterWaehlen(neu: readonly RechnungsFilter[]): void {
    setQuittung(null);
    setBearbeitetId(null);
    setOffset(0);
    // Der SUCHTEXT bleibt ausdrücklich stehen: „Wo steht die Rechnung von X?" ist eine
    // Frage, die man durch die Gruppen HINDURCH stellt — erst unter Festgeschrieben, dann
    // unter Bezahlt. Ihn beim Filterwechsel zu leeren zwänge zum Nachtippen bei jedem
    // Schritt. Die Blätterung geht dagegen auf Seite 1 zurück.
    p.onFilter(neu);
  }

  /** Das Kürzel des Unternehmens zu einer Zeile. Fehlt die Firma (sie könnte stillgelegt
   *  sein), bleibt die Stelle leer statt eine nackte UUID zu zeigen. */
  function kuerzel(id: string): string {
    return companies.find((c) => c.id === id)?.shortCode ?? "";
  }

  const gefiltert = suche !== "" || !alleChips;
  const von = seite === null || seite.total === 0 ? 0 : offset + 1;
  const bis = seite === null ? 0 : Math.min(offset + seite.items.length, seite.total);

  const spalte = (
    <div className="brng">
      {/*
        Der FILTERKOPF (components/Filterkopf.tsx) — Suchfeld und Chip-Leiste in einem
        Baustein, den alle vier Objektbereiche teilen. Die Suche wirkt auf die
        eingeschalteten Gruppen; Zusatzfilter hat der Rechnungs-Bereich keine, und einen
        Anlegen-Knopf ebenso wenig: Eine Rechnung entsteht am Auftrag, nicht auf Zuruf.

        `lage="spalte"` seit dem 23.09.2026 (Entscheid Dirk): Er steht oben in der
        Listenspalte des Rahmens. Die vier Chips brechen in der schmalen Spalte um — das
        konnten sie schon (`fkop__chips` mit `flex-wrap`).
      */}
      <Filterkopf
        lage="spalte"
        suchLabel="Rechnungen durchsuchen"
        platzhalter="Nummer oder Kunde"
        suche={suche}
        onSuche={(t) => filtern(() => setSuche(t))}
        gesperrt={bearbeitung}
        chips={{
          label: "Rechnungen nach Status",
          alle: "Alle Rechnungen",
          werte: CHIPS,
          modus: "mehrfach",
          aktiv: filter,
          onAktiv: filterWaehlen,
        }}
      />

      {/* Die BELEGLISTE. Sie ROLLT (siehe BereichRechnungen.css): Der Filterkopf mit seinen
          vier Chips bleibt oben stehen. */}
      <div className="brng__liste">
        {ladeFehler !== null && (
          <p className="brng__fehler" role="alert">{ladeFehler}</p>
        )}

        {ladeFehler === null && keineAuswahl && (
          <p className="brng__hinweis">
            Kein Status gewählt — bitte oben mindestens einen einschalten.
          </p>
        )}

        {ladeFehler === null && !keineAuswahl && seite === null && (
          <p className="brng__lade" role="status">Lädt …</p>
        )}

        {ladeFehler === null && !keineAuswahl && seite !== null
          && seite.items.length === 0 && (
          <p className="brng__hinweis">
            {gefiltert
              ? "Zu dieser Auswahl gibt es keine Rechnung."
              : "Es gibt noch keine Rechnungen."}
          </p>
        )}

        {/*
          Die Zeilen tragen den gemeinsamen Baustein (Entscheid Dirk 23.09.2026) — dieselbe
          Anatomie wie in den drei Schwesterbereichen. Der Inhalt ist unverändert, nur in die
          Anatomie gelegt:

           - Die NUMMER ist die Identität des Belegs und steht als `kennung`; ein Entwurf hat
             noch keine, dort steht „Entwurf" an ihrer Stelle.
           - Der KUNDE ist der Name, unter dem man den Beleg sucht — er steht als `name`.
           - Das FORMAT geht als ruhige MARKE mit und nicht als zweiter Chip: Es ist reine
             Anzeige und kein Filter (danach sucht niemand, dafür stehen die vier
             Status-Chips oben). Zwei Chips nebeneinander sähen aus wie zwei Zustände.
           - Zeile 2 nennt Kürzel, Rechnungsdatum, „Korrektur" und den BRUTTO-Betrag —
             was eine Rechnung kostet, ist ihr Bruttobetrag; gerechnet hat ihn der Server.
        */}
        <Objektliste label="Rechnungen">
          {(keineAuswahl ? [] : seite?.items ?? []).map((r) => (
            <Objektzeile
              key={r.id}
              name={r.customerName}
              kennung={r.invoiceNumber ?? "Entwurf"}
              meta={[
                kuerzel(r.companyId),
                r.invoiceDate === null
                  ? "ohne Rechnungsdatum" : formatDatum(r.invoiceDate),
                // Dass eine Zeile eine KORREKTUR ist, sieht man ihr sonst nur am negativen
                // Betrag an — und das ist zu wenig.
                r.correctsInvoiceId === null ? "" : "Korrektur",
                formatBetrag(r.grossAmount, "EUR"),
              ].filter((t) => t !== "").join(" · ")}
              status={{
                text: RECHNUNG_ANZEIGE_LABEL[r.anzeigeStatus],
                art: STATUS_ART[r.anzeigeStatus],
              }}
              /* Das Rechnungsformat als Marke. Bekannt → die kurze Bezeichnung aus der
                 Tabelle. Unbekannt, aber ein Wert kam mit dem Treffer (ein künftiges Format,
                 das diese Tabelle noch nicht kennt) → der Rohwert, damit er AUFFÄLLT statt
                 stillschweigend zu verschwinden — ein `undefined` in der Markenliste
                 zeichnete sonst eine leere Pille und liesse React über einen fehlenden
                 Schlüssel klagen (Befund Aufgabe 7, 23.09.2026; Nachtrag Fixrunde
                 23.09.2026). Sonst KEINE Marke. */
              marken={((f) => {
                if (f !== undefined) return [f.kurz];
                return typeof r.invoiceFormat === "string" ? [r.invoiceFormat] : [];
              })(RECHNUNGSFORMATE.find((f) => f.wert === r.invoiceFormat))}
              gewaehlt={r.id === (rechnungId ?? p.markiertId ?? null)}
              gesperrt={bearbeitung}
              /* Der zugängliche Name nennt Nummer UND Kunden; zwanzig Zeilen „Öffnen" wären
                 für jemanden, der die Seite hört, nicht unterscheidbar. */
              ariaLabel={`Rechnung ${r.invoiceNumber ?? "Entwurf"}, `
                + `${r.customerName}, anzeigen`}
              onOeffnen={() => adresseZu(r.id)}
            />
          ))}
        </Objektliste>

        {!keineAuswahl && seite !== null && seite.total > 0 && (
          <div className="brng__blaettern">
            <button type="button" className="brng__knopf"
              disabled={offset === 0 || bearbeitung}
              onClick={() => setOffset((o) => Math.max(0, o - JE_SEITE))}>
              Zurück
            </button>
            <span className="brng__zusatz">{von}–{bis} von {seite.total}</span>
            <button type="button" className="brng__knopf"
              disabled={offset + seite.items.length >= seite.total || bearbeitung}
              onClick={() => setOffset((o) => o + JE_SEITE)}>
              Weiter
            </button>
          </div>
        )}
      </div>
    </div>
  );

  /*
   * Die Detailfläche bekommt bewusst KEINE eigene Landmarke: Der Rahmen zeichnet dort bereits
   * eine (MeSchale), und eine zweite Region namens „Rechnungen" wäre mit der Spalte daneben
   * verwechselbar.
   */
  const detail = (
    <div className="brng__inhalt">
      {hinweis !== null && (
        <p className={MELDUNGS_STIL[hinweis.art].klasse}
          role={MELDUNGS_STIL[hinweis.art].rolle}>
          {hinweis.text}
        </p>
      )}
      {satzFehler !== null && <p className="brng__fehler" role="alert">{satzFehler}</p>}

      {rechnungId === null ? (
        <p className="brng__leer">
          Links eine Rechnung wählen — sie steht dann hier, mit allem, was
          auch die Kundenakte zeigt.
        </p>
      ) : bezug === null ? (
        /*
         * Ohne Kundenbezug lässt sich der Beleg nicht lesen (siehe den Kopf dieser
         * Datei) — aber „nicht in der Trefferliste" darf erst behauptet werden, wenn es
         * eine gibt. Solange die Suche läuft, wäre die Aussage schlicht falsch; und wenn
         * sie gescheitert ist, liegt es nicht am Beleg, sondern an der Liste.
         */
        ladeFehler !== null ? (
          <p className="brng__leer">
            Die Rechnungsliste konnte nicht geladen werden — ohne sie lässt sich diese
            Rechnung nicht öffnen. Bitte die Seite neu laden.
          </p>
        ) : seite === null ? (
          <p className="brng__leer">Lädt …</p>
        ) : (
          <p className="brng__leer">
            Diese Rechnung steht nicht in der Trefferliste — bitte oben über „Alle
            Rechnungen" oder die Suche öffnen.
          </p>
        )
      ) : satz === null ? (
        satzFehler === null ? <p className="brng__leer">Lädt …</p> : <></>
      ) : (
        <div className="brng__karte">
          <div className="brng__detail-kopf">
            <span className="brng__kunde">{bezug.kundeName}</span>
            {/* Der Kundenbezug bleibt SICHTBAR und begehbar: Die Akte ist weiterhin der
                eine Ort, an dem alles zu einem Kunden steht — der Bereich nimmt ihr nur die
                Pflicht, für jeden Beleg der Umweg zu sein. Der Rückweg von dort kennt seine
                Herkunft (App.tsx, `zurueckWeg`). */}
            <button type="button" className="brng__zum-kunden"
              onClick={() => p.onZumKunden(bezug.kundeId)}>
              Zum Kunden
            </button>
          </div>

          <RechnungDetail
            /*
             * EIGENE Identität je Beleg (Fixrunde 1, Review-Befund). Der Reiter bekommt
             * sie geschenkt — dort ist jede Listenzeile eine eigene Instanz. Hier steht an
             * derselben Stelle nacheinander ein ANDERER Beleg, und ohne diesen Schlüssel
             * behielte der Baustein seinen Zustand über den Wechsel hinweg. Gemessen wurde
             * beides:
             *
             *  - Der Festschreib-Kasten lässt sich am GESCHLOSSENEN Eintrag öffnen (dort
             *    sperrt nichts die Spalte). Wer danach eine andere Zeile anklickt, hätte
             *    den Kasten von Beleg A über Beleg B stehen — und schriebe B mit dem Datum
             *    von A fest, selbst wenn B längst festgeschrieben ist.
             *  - Der Sprung Original↔Korrektur wechselt den Beleg, ohne `offen` zu ändern:
             *    Render-Modell, Positionen und Ablagestand des Originals blieben stehen,
             *    und `renderModellLaden` holte das richtige gar nicht erst nach (es kehrt
             *    bei einem bereits geladenen Modell früh zurück).
             *
             * Der Schlüssel löst beides an der Wurzel, statt jeden Zustand einzeln
             * nachzuräumen: Ein anderer Beleg ist eine andere Instanz.
             */
            key={satz.id}
            client={client}
            kundeId={bezug.kundeId}
            rechnung={satz}
            companies={companies}
            umfeld={umfeld}
            offen={bearbeitung}
            // Es gibt hier genau EIN Detail — was der Reiter mit `gesperrt` gegen seine
            // übrigen Zeilen absichert, kann im Bereich gar nicht auftreten.
            gesperrt={false}
            kundeName={bezug.kundeName}
            onOeffnen={() => setBearbeitetId(satz.id)}
            onSchliessen={() => setBearbeitetId(null)}
            onNeuLaden={satzNeuLaden}
            onMeldung={melde}
            onFortschreiben={fortschreiben}
            onSpringeZuRechnung={(id) => aufschlagen(id)}
            onKorrekturAngelegt={korrekturAufnehmen}
            onBearbeitungWechsel={setFormularOffen} />
        </div>
      )}
    </div>
  );

  return { spalte, detail };
}
